import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userQueryKeys } from '@entities/user';
import { assistantApi } from './assistantApi';
import type { Assistant, AssistantCreate, AssistantPatch } from '../model/types';

/**
 * Ключи запросов для ассистентов.
 *
 * После любой мутации (create/patch/delete) инвалидируем:
 * 1) свои ключи (assistantQueryKeys.detail(id), .list)
 * 2) userQueryKeys.me() — там лежит user.assistants для сайдбара
 */
export const assistantQueryKeys = {
  all: ['assistants'] as const,
  detail: (id: string) => [...assistantQueryKeys.all, 'detail', id] as const,
};

/**
 * Хук получения деталей ассистента.
 * include — например 'sources,threads' для подгрузки в одном запросе.
 */
export function useAssistant(id: string | undefined, include?: string) {
  return useQuery<Assistant, Error>({
    queryKey: include
      ? [...assistantQueryKeys.detail(id ?? ''), include]
      : assistantQueryKeys.detail(id ?? ''),
    queryFn: () => assistantApi.get(id!, include),
    enabled: !!id,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403 || status === 404) return false;
      return failureCount < 1;
    },
  });
}

export function useCreateAssistantMutation() {
  const qc = useQueryClient();
  return useMutation<Assistant, Error, AssistantCreate>({
    mutationFn: (payload) => assistantApi.create(payload),
    onSuccess: () => {
      // user.assistants содержит список — обновляем
      qc.invalidateQueries({ queryKey: userQueryKeys.me() });
      // Главная и левое меню (статусы, модель) — GET /cabinet/home
      qc.invalidateQueries({ queryKey: ['cabinet'] });
    },
  });
}

export function usePatchAssistantMutation(id: string) {
  const qc = useQueryClient();
  return useMutation<Assistant, Error, AssistantPatch>({
    mutationFn: (payload) => assistantApi.patch(id, payload),
    onSuccess: (data) => {
      // Обновляем кэш детали оптимистично
      qc.setQueryData(assistantQueryKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: userQueryKeys.me() });
      // Главная и левое меню (статусы, модель) — GET /cabinet/home
      qc.invalidateQueries({ queryKey: ['cabinet'] });
    },
  });
}

export function useDeleteAssistantMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => assistantApi.delete(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: assistantQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: userQueryKeys.me() });
      // Главная и левое меню (статусы, модель) — GET /cabinet/home
      qc.invalidateQueries({ queryKey: ['cabinet'] });
    },
  });
}
